import { type NoiseSpec, type Rig, type ToneSpec, noise, tone } from "./rig";

/**
 * What the games actually sound like.
 *
 * One vocabulary across all twenty-one of them, deliberately - a tile turning
 * over in mines and a gem turning over in diamonds are the same event to the
 * player, and giving each game its own bespoke ding is how a site ends up
 * sounding like twenty-one different sites. So the names here are *events*, not
 * games: something was picked, something was revealed, a round was staked, a
 * round paid. A game that wants its own character gets it by pitching a shared
 * voice (`rate`) rather than by inventing a new one.
 *
 * Volumes are all over the 0.1–0.5 range on purpose. These are mixed relative
 * to one another, not set individually: a peg strike fires fourteen times a
 * ball and a cash-out fires once a round, so they cannot be the same loudness
 * and feel balanced.
 */
export type SoundName =
  // Staking and the controls around it.
  | "click"
  | "chip"
  | "bet"
  | "clear"
  // Cards.
  | "deal"
  | "flip"
  | "shuffle"
  // Things happening on the board.
  | "reveal"
  | "hit"
  | "miss"
  | "peg"
  | "drop"
  | "land"
  | "whoosh"
  | "tick"
  | "coin"
  // How a round ended.
  | "win"
  | "bigWin"
  | "lose"
  | "push"
  | "cashout"
  | "explode";

export interface VoiceOptions {
  /**
   * Pitch multiplier for this one hit.
   *
   * The reason the vocabulary stays small: a keno card rising through its hits,
   * a mines ladder climbing, a plinko ball getting shallower as it falls are
   * all the same voice at a different pitch. 1 is the voice as written.
   */
  rate?: number;
  /** Loudness multiplier for this one hit, over whatever the voice asks for. */
  gain?: number;
}

type Voice = (rig: Rig, at: number, options: VoiceOptions) => void;

/** `tone`, with this hit's pitch and loudness folded in. */
const note = (rig: Rig, at: number, spec: ToneSpec, { rate = 1, gain = 1 }: VoiceOptions) =>
  tone(rig, at, {
    ...spec,
    freq: spec.freq * rate,
    to: spec.to === undefined ? undefined : spec.to * rate,
    gain: (spec.gain ?? 0.3) * gain,
  });

/** `noise`, the same way. */
const knock = (rig: Rig, at: number, spec: NoiseSpec, { rate = 1, gain = 1 }: VoiceOptions) =>
  noise(rig, at, {
    ...spec,
    freq: (spec.freq ?? 900) * rate,
    to: spec.to === undefined ? undefined : spec.to * rate,
    gain: (spec.gain ?? 0.3) * gain,
  });

export const VOICES: Record<SoundName, Voice> = {
  /** A pick, a deselect, a toggle. The smallest sound here. */
  click: (rig, at, o) => {
    knock(rig, at, { freq: 1900, q: 2, decay: 0.028, gain: 0.16 }, o);
    note(rig, at, { freq: 1250, decay: 0.03, gain: 0.07 }, o);
  },

  /** A chip set down on a layout. Clay, not plastic: there is a body to it. */
  chip: (rig, at, o) => {
    knock(rig, at, { freq: 2600, q: 1.4, decay: 0.022, gain: 0.2 }, o);
    knock(rig, at, { freq: 1150, q: 2.2, decay: 0.05, gain: 0.12, delay: 0.018 }, o);
    note(rig, at, { freq: 190, to: 150, decay: 0.07, gain: 0.1 }, o);
  },

  /** The stake is in. Two notes up, because the round is now going somewhere. */
  bet: (rig, at, o) => {
    note(rig, at, { freq: 520, type: "triangle", decay: 0.07, gain: 0.2 }, o);
    note(rig, at, { freq: 784, type: "triangle", decay: 0.11, gain: 0.16, delay: 0.055 }, o);
  },

  /** The board swept, the bets taken back. The same two notes, downward. */
  clear: (rig, at, o) => {
    note(
      rig,
      at,
      { freq: 520, to: 300, type: "triangle", decay: 0.12, gain: 0.13, cutoff: 2400 },
      o,
    );
  },

  /** A card off the shoe: all air, no pitch. */
  deal: (rig, at, o) => {
    knock(rig, at, { type: "highpass", freq: 2400, to: 900, q: 0.6, decay: 0.075, gain: 0.2 }, o);
  },

  /** A card turned face up. The flick, then the slap. */
  flip: (rig, at, o) => {
    knock(rig, at, { type: "highpass", freq: 1800, to: 3200, q: 0.7, decay: 0.045, gain: 0.16 }, o);
    knock(rig, at, { freq: 1100, q: 1.6, decay: 0.05, gain: 0.14, delay: 0.05 }, o);
  },

  /** A fresh shoe. Six riffles, slightly uneven, because a tidy one is a tell. */
  shuffle: (rig, at, o) => {
    for (let i = 0; i < 6; i += 1) {
      knock(
        rig,
        at,
        {
          type: "highpass",
          freq: 1700 + i * 240,
          q: 0.5,
          decay: 0.06,
          gain: 0.12,
          delay: i * 0.045 + Math.random() * 0.012,
        },
        o,
      );
    }
  },

  /**
   * Something good turned over: a gem, a safe tile, a lane that held.
   *
   * Pitch it by how far up the ladder the player is and the board plays its own
   * rising scale - which is the whole reason `rate` exists.
   */
  reveal: (rig, at, o) => {
    note(rig, at, { freq: 880, type: "triangle", decay: 0.16, gain: 0.2, cutoff: 5200 }, o);
    note(rig, at, { freq: 1320, decay: 0.2, gain: 0.1, delay: 0.025 }, o);
  },

  /** A drawn number that was one of theirs. */
  hit: (rig, at, o) => {
    note(rig, at, { freq: 660, type: "triangle", decay: 0.13, gain: 0.18, cutoff: 4200 }, o);
    note(rig, at, { freq: 990, decay: 0.1, gain: 0.08, delay: 0.02 }, o);
  },

  /** A drawn number that was not. Low and short: it is information, not drama. */
  miss: (rig, at, o) => {
    note(
      rig,
      at,
      { freq: 260, to: 210, type: "triangle", decay: 0.1, gain: 0.13, cutoff: 1200 },
      o,
    );
  },

  /** A ball off a peg. Narrow band, so it reads as wood rather than a hiss. */
  peg: (rig, at, o) => {
    knock(rig, at, { freq: 1500, q: 7, decay: 0.03, gain: 0.2 }, o);
  },

  /** A ball let go. */
  drop: (rig, at, o) => {
    note(rig, at, { freq: 340, to: 150, decay: 0.12, gain: 0.18, cutoff: 1800 }, o);
    knock(rig, at, { type: "highpass", freq: 1400, to: 600, decay: 0.07, gain: 0.1 }, o);
  },

  /** It settled - a pocket, a slot, a stopped wheel. */
  land: (rig, at, o) => {
    note(rig, at, { freq: 196, to: 150, type: "triangle", decay: 0.22, gain: 0.24 }, o);
    knock(rig, at, { freq: 620, q: 3, decay: 0.08, gain: 0.16 }, o);
  },

  /** A wheel launched, a reel thrown, a rocket leaving. */
  whoosh: (rig, at, o) => {
    knock(
      rig,
      at,
      { type: "highpass", freq: 320, to: 4200, q: 0.4, attack: 0.07, decay: 0.3, gain: 0.2 },
      o,
    );
  },

  /** A pointer past a segment, or a second off a clock. */
  tick: (rig, at, o) => {
    knock(rig, at, { freq: 3000, q: 9, decay: 0.018, gain: 0.13 }, o);
  },

  /** Metal in the air, for the half-second a coin is up there. */
  coin: (rig, at, o) => {
    knock(rig, at, { freq: 4200, q: 6, decay: 0.05, gain: 0.12 }, o);
    note(rig, at, { freq: 1046, decay: 0.07, gain: 0.09 }, o);
    note(rig, at, { freq: 1318, decay: 0.07, gain: 0.08, delay: 0.055 }, o);
    note(rig, at, { freq: 1568, decay: 0.09, gain: 0.07, delay: 0.11 }, o);
  },

  /** It paid. A major triad, up. */
  win: (rig, at, o) => {
    [523.25, 659.25, 783.99].forEach((freq, i) => {
      note(
        rig,
        at,
        { freq, type: "triangle", decay: 0.22, gain: 0.17, cutoff: 5200, delay: i * 0.065 },
        o,
      );
    });
    note(rig, at, { freq: 1046.5, decay: 0.3, gain: 0.09, delay: 0.2 }, o);
  },

  /**
   * It paid *properly*.
   *
   * The same triad carried up an octave and left to ring, with a sparkle over
   * the top. Reserved for the rounds that deserve it - a game that plays this
   * on every win has no way left to say a hundred times the stake.
   */
  bigWin: (rig, at, o) => {
    [523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((freq, i) => {
      note(
        rig,
        at,
        { freq, type: "triangle", decay: 0.34, gain: 0.16, cutoff: 6000, delay: i * 0.075 },
        o,
      );
    });
    [1567.98, 2093].forEach((freq, i) => {
      note(rig, at, { freq, decay: 0.5, gain: 0.08, delay: 0.38 + i * 0.09 }, o);
    });
    for (let i = 0; i < 4; i += 1) {
      knock(
        rig,
        at,
        { freq: 5200 + i * 700, q: 5, decay: 0.07, gain: 0.07, delay: 0.42 + i * 0.07 },
        o,
      );
    }
  },

  /** It did not. Dull, falling, over quickly. */
  lose: (rig, at, o) => {
    note(rig, at, { freq: 233, to: 110, type: "triangle", decay: 0.3, gain: 0.18, cutoff: 800 }, o);
    knock(rig, at, { type: "lowpass", freq: 320, decay: 0.18, gain: 0.1 }, o);
  },

  /** Nothing happened. Two flat blips, saying exactly that. */
  push: (rig, at, o) => {
    note(rig, at, { freq: 440, type: "square", decay: 0.07, gain: 0.1, cutoff: 1400 }, o);
    note(
      rig,
      at,
      { freq: 440, type: "square", decay: 0.09, gain: 0.1, cutoff: 1400, delay: 0.12 },
      o,
    );
  },

  /** Taken off the table. A register: the strike, the bell, then the coins. */
  cashout: (rig, at, o) => {
    knock(rig, at, { freq: 3200, q: 4, decay: 0.03, gain: 0.18 }, o);
    note(rig, at, { freq: 1568, decay: 0.32, gain: 0.17, cutoff: 6000 }, o);
    note(rig, at, { freq: 2093, decay: 0.38, gain: 0.1, delay: 0.035 }, o);
    note(rig, at, { freq: 392, type: "triangle", decay: 0.2, gain: 0.1, delay: 0.02 }, o);
    for (let i = 0; i < 3; i += 1) {
      knock(
        rig,
        at,
        { freq: 4400 + i * 500, q: 7, decay: 0.05, gain: 0.07, delay: 0.14 + i * 0.06 },
        o,
      );
    }
  },

  /** A mine, a crash, a shot saved. The only sound here with real weight. */
  explode: (rig, at, o) => {
    knock(rig, at, { type: "lowpass", freq: 900, to: 70, q: 0.5, decay: 0.45, gain: 0.45 }, o);
    note(rig, at, { freq: 110, to: 42, decay: 0.4, gain: 0.3 }, o);
    knock(rig, at, { type: "highpass", freq: 2600, decay: 0.09, gain: 0.16 }, o);
  },
};

/**
 * The shortest gap, in milliseconds, between two of the same voice.
 *
 * Not a performance guard - the rig can take it - but a listening one. Two
 * identical hits inside about fifteen milliseconds do not read as two hits,
 * they read as one hit with a phasing artefact, and that is what an auto run
 * firing a board a frame at a time produces. The impacts get the tightest gaps
 * because they are the ones that genuinely come in runs; a verdict gets a long
 * one because hearing two of them at once would be a bug either way.
 */
export const VOICE_GAP: Record<SoundName, number> = {
  click: 28,
  chip: 40,
  bet: 60,
  clear: 60,
  deal: 32,
  flip: 32,
  shuffle: 400,
  reveal: 28,
  hit: 20,
  miss: 20,
  peg: 14,
  drop: 50,
  land: 60,
  whoosh: 120,
  tick: 22,
  coin: 120,
  win: 160,
  bigWin: 400,
  lose: 160,
  push: 160,
  cashout: 200,
  explode: 200,
};
